import { iParsedEntity, iParsedEntityWithDisplay } from '../interfaces/iEntityParsed';
import { iMasterTable } from '../interfaces/iMasterTable';

/** Lowercase and strip underscores/spaces so "Client Loc" matches "client_loc" / "ClientLoc". */
const normalize = (str: string) => (str || '').toLowerCase().replace(/[_\s]+/g, '');

/**
 * Unique entity id from physical table name.
 * `client_loc` → `ClientLoc`. Single tokens keep existing casing (`AppleSrc` → `AppleSrc`).
 */
function toPascalCaseEntityName(value: string): string {
    if (!value) {
        return '';
    }
    if (!/[_\s]/.test(value)) {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
}

/**
 * Match on unique identifiers only.
 * Do NOT use MasterTableDomainNm — it is a shared category (Client/Product/Vendor).
 */
function findMatchingMasterTable(entityName: string, masterTables: iMasterTable[]): iMasterTable | undefined {
    const entityNameNorm = normalize(entityName);

    return masterTables.find((table) => {
        const candidates = [table.MasterTableNm, table.MasterTableUiLabelTxt, table.MasterTableOdataNm, toPascalCaseEntityName(table.MasterTableNm)];
        return candidates.some((candidate) => candidate && normalize(candidate) === entityNameNorm);
    });
}

export function filterEntities(value: string, options: iParsedEntity[], masterTables: iMasterTable[]): iParsedEntityWithDisplay[] {
    const filterValue = (value || '').toLowerCase();

    return options
        .map((option) => {
            const masterTable = findMatchingMasterTable(option.entityName, masterTables);
            if (!masterTable) {
                return null;
            }
            return {
                ...option,
                // Unique id from MasterTableNm — never MasterTableDomainNm (shared category).
                entityName: toPascalCaseEntityName(masterTable.MasterTableNm) || option.entityName,
                displayName: masterTable.MasterTableUiLabelTxt || option.entityName,
            };
        })
        .filter((option): option is iParsedEntityWithDisplay => option !== null)
        .filter((option) => option.displayName.toLowerCase().includes(filterValue))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
